import { ComponentSize } from '@eleventheye/asui';
import { TeamNamesByID } from '@shared/constants';
import { breakTeamAbrvID, toTeamAbrvID } from '@shared/helpers';
import { TeamAbrvID } from '@shared/types';
import { Cls, Emo } from 'app/constants';
import TeamLogo from 'app/scenes/statsmanager/components/teamlogo/TeamLogo';
import ASBox from 'components/asbox/ASBox';
import React, { useCallback, useEffect, useState } from 'react';
import styles from './TeamPicker.module.scss';

type TeamPickerProps = {
  className?: string;
  disabledTeams?: TeamAbrvID[];
  onDisabledTeamsChanged?: (disabledTeams: TeamAbrvID[]) => void;
  selectedTeams?: TeamAbrvID[];
  onSelectionChanged?: (selectedTeams: TeamAbrvID[]) => void;
};

const createTeamAbrvIDs = (): TeamAbrvID[] => {
  const teamAbrvIDs: TeamAbrvID[] = [];

  TeamNamesByID.forEach((team, index) => {
    if (team !== 'All') teamAbrvIDs.push(toTeamAbrvID(`${index}`));
  });

  return teamAbrvIDs;
};
type TeamPickerItemProps = {
  className?: string;
  teamAbrvID: TeamAbrvID;
  disabled?: boolean;
  selected?: boolean;
  onItemTapped?: (teamAbrvID: TeamAbrvID) => void;
  onDisabledItemTapped?: (teamAbrvID: TeamAbrvID) => void;
};

const TeamPickerItem: React.FC<TeamPickerItemProps> = ({
  className,
  teamAbrvID,
  disabled = false,
  selected = false,
  onItemTapped,
  onDisabledItemTapped,
}) => {
  const disabledCls = disabled ? styles.TeamPickerItemDisabled : '';
  const selectedCls = selected ? styles.TeamPickerItemSelected : '';
  const baseCls = `${styles.TeamPickerItemContainer} ${className} ${disabledCls} ${selectedCls}`;
  const { teamAbrv } = breakTeamAbrvID(teamAbrvID);

  const onItemTappedHandler = () => onItemTapped?.(teamAbrvID);
  const onDisabledItemTappedHandler = () => onDisabledItemTapped?.(teamAbrvID);

  const onItemTappedFinalHandler = disabled ? onDisabledItemTappedHandler : onItemTappedHandler;

  return (
    <div className={baseCls} onClick={onItemTappedFinalHandler}>
      <TeamLogo teamName={teamAbrv} iconType="v1" size={ComponentSize.Small} />
      <span className={styles.Label}>{teamAbrv}</span>
    </div>
  );
};

const TeamPicker: React.FC<TeamPickerProps> = ({
  className = '',
  selectedTeams = [],
  onSelectionChanged,
  disabledTeams = [],
  onDisabledTeamsChanged,
}) => {
  const baseCls = `${styles.TeamPickerContainer} ${className}`;
  const allTeams = createTeamAbrvIDs();

  const [selectedTeamsLocal, setSelectedTeamsLocal] = useState<TeamAbrvID[]>([]);
  const [disabledTeamsLocal, setDisabledTeamsLocal] = useState<TeamAbrvID[]>([]);

  useEffect(() => {
    console.log(`${Emo.eyes} selectedTeams %c ${selectedTeams.toString()} %c`, Cls.Army, '');
    setSelectedTeamsLocal(selectedTeams);
  }, [selectedTeams]);

  useEffect(() => {
    console.log(`${Emo.eyes} disabledTeams %c ${disabledTeams.toString()} %c`, Cls.Cri, '');
    setDisabledTeamsLocal(disabledTeams);
  }, [disabledTeams]);

  const onTeamTapped = useCallback(
    (teamAbrvID: TeamAbrvID) => {
      let teamsLocal: TeamAbrvID[] = [];
      if (selectedTeamsLocal.includes(teamAbrvID)) {
        teamsLocal = selectedTeamsLocal.filter((team) => team !== teamAbrvID);
      } else {
        teamsLocal = [...selectedTeamsLocal, teamAbrvID];
      }
      setSelectedTeamsLocal(teamsLocal);
      onSelectionChanged?.(teamsLocal);
    },
    [selectedTeamsLocal, onSelectionChanged],
  );

  const onDisabledTeamTapped = useCallback(
    (teamAbrvID: TeamAbrvID) => {
      let teamsLocal: TeamAbrvID[] = [];
      if (disabledTeamsLocal.includes(teamAbrvID)) {
        teamsLocal = disabledTeamsLocal.filter((team) => team !== teamAbrvID);
      } else {
        teamsLocal = [...disabledTeamsLocal, teamAbrvID];
      }
      setDisabledTeamsLocal(teamsLocal);
      onDisabledTeamsChanged?.(teamsLocal);
    },
    [disabledTeamsLocal, onDisabledTeamsChanged],
  );

  return (
    <div className={baseCls}>
      <ASBox darkBg className={styles.TeamPickerBox}>
        {allTeams.map((team) => (
          <TeamPickerItem
            key={team}
            teamAbrvID={team}
            onItemTapped={onTeamTapped}
            onDisabledItemTapped={onDisabledTeamTapped}
            selected={selectedTeamsLocal.includes(team)}
            disabled={disabledTeamsLocal.includes(team)}
          />
        ))}
      </ASBox>
    </div>
  );
};

export default TeamPicker;
